// Wallet preferences settings — sellers-only placeholder.
//
// Volkan direction 2026-05-23 (Settings 10-section expansion item #6):
// Complements the sidebar Wallet submenu (live since 2026-05-23). Settings
// provides preference toggles (auto-withdraw threshold, statement cadence,
// notification preferences) — wallet operations live in the sidebar Wallet
// surface. Backend persistence M3 Stage 4 SPEC.md Faz 4 (Wallets + Settlement).

import { ArrowRight, Wallet } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { walletPreferencesI18n } from '../../i18n/settings/wallet-preferences'
import { useToastStore } from '../../lib/toast'
import type { SettingsSurface } from './Settings'

type PreferenceId =
  | 'autoWithdraw'
  | 'statementCadence'
  | 'payoutMethod'
  | 'notifications'

type PreferenceRow = {
  id: PreferenceId
  value: string
}

// Operational values preserved as proper-noun-style data (USD ACH rail
// signature, EST schedule); only chrome translates.
const PREFERENCES: PreferenceRow[] = [
  { id: 'autoWithdraw', value: '$500.00' },
  { id: 'statementCadence', value: 'Weekly · Mondays 9:00 EST' },
  { id: 'payoutMethod', value: 'ACH · Chase ****4218' },
  { id: 'notifications', value: '' }, // resolved at render via t.preferences.notifications.enabled
]

export function WalletPreferences({ surface }: { surface: SettingsSurface }) {
  void surface // sellers-only filter at Settings shell level
  const push = useToastStore((s) => s.push)
  const t = useT(walletPreferencesI18n)

  const notifyMock = (label: string) =>
    push({
      variant: 'info',
      title: `${label} · mock-only`,
      description: t.toastTail,
      duration_ms: 4000,
    })

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-slate-900">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-slate-600">
          {t.pageDescriptionLead}
          <span className="font-medium text-slate-900">
            {t.pageDescriptionWalletKeyword}
          </span>
          {t.pageDescriptionTail}
        </p>
      </header>

      <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
        <div className="flex items-start gap-3">
          <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
          <div className="flex-1 text-sm text-sky-900">
            <p className="font-medium">{t.walletShortcut.title}</p>
            <p className="mt-1 text-xs text-sky-800">
              {t.walletShortcut.description}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              push({
                variant: 'info',
                title: t.walletShortcut.toastTitle,
                description: t.walletShortcut.toastDescription,
                duration_ms: 3000,
              })
            }
          >
            {t.walletShortcut.action}
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <ul className="divide-y divide-slate-100">
          {PREFERENCES.map((row) => {
            const pref = t.preferences[row.id]
            const display =
              row.id === 'notifications'
                ? t.preferences.notifications.enabled
                : row.value
            return (
              <li
                key={row.id}
                className="flex items-start justify-between gap-4 px-5 py-4"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">
                    {pref.label}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {pref.description}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-700">{display}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => notifyMock(pref.label)}
                  >
                    {t.actions.change}
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      <p className="text-xs text-slate-400">{t.footerNote}</p>
    </div>
  )
}
